import type { DraftState, WizardFile, WizardReleasedFile, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.ts'
import { SampleConfigurationTypes } from '../../Generated/SampleConfigurationType.ts'
import { expectsReverseFile } from '../../Util/reverseFilePairing.ts'
import { detectReadLayout, nameMate } from '../../Util/orderSettingDetection/readLayoutDetection.ts'
import { isSampleConfigurationAllowedForPlatform } from '../../Util/sequencingPlatform.ts'
import { plural } from '../../Util/plural.ts'
import { placedGroups } from './pools.ts'
import { layoutSwitchOf } from './orderSettingsPlan.ts'
import { countItems } from './sampleLabels.ts'
import { withRemovalsRetried } from './removal.ts'

/*
 * Samples of a paired-end order that miss their R2 — what a merged-to-paired switch leaves behind — and
 * the R2 files that complete them: dropped later, uploaded already, or turned into samples of their own.
 */

/** Whether the draft's order takes an R1/R2 pair per sample. */
export const expectsMates = (draft: Pick<DraftState, 'settings'>): boolean =>
  !!draft.settings && expectsReverseFile(draft.settings.sampleConfiguration.value)

/* Nanopore files are single-ended whatever the format says */
const singleEnded = (draft: DraftState, group: WizardSampleGroup) => draft.files[group.forward]?.platform === 'ONT'

export const missingReverse = (draft: DraftState, group: WizardSampleGroup): boolean =>
  expectsMates(draft) && !group.reverse && group.item.status !== 'removed' && !singleEnded(draft, group)

export const groupsMissingReverse = (draft: DraftState): WizardSampleGroup[] =>
  placedGroups(draft).filter((group) => missingReverse(draft, group))

/**
 * `absorb`: the source is an item of its own, deleted as its file joins the target. `take`: the source never
 * became an item, its file simply joins. `released`: an upload no sample uses joins.
 */
export type ReverseJoin =
  | { kind: 'absorb'; target: string; source: string }
  | { kind: 'take'; target: string; source: string }
  | { kind: 'released'; target: string; file: WizardReleasedFile }

const hasNoItem = (group: WizardSampleGroup) => group.item.status === 'waiting' || group.item.status === 'failed'

type Entry = { name: string; group?: WizardSampleGroup; released?: WizardReleasedFile }

/**
 * The single-file samples paired with their R2 by the R1/R2 file naming, as the read-layout detector pairs a
 * batch: the R2 is another single-file sample or an upload no sample uses. A sample is only ever the R1 side.
 * A sample without an item cannot take the file of one that has, which would leave a file backing two items.
 */
export const planReverseJoins = (draft: DraftState): ReverseJoin[] => {
  const groups = placedGroups(draft).filter(
    (group) => !group.reverse && !singleEnded(draft, group) && group.item.status !== 'creating' && !!draft.files[group.forward]
  )
  const entries: Entry[] = [
    ...groups.map((group) => ({ name: draft.files[group.forward].name, group })),
    ...(draft.releasedFiles ?? []).map((released) => ({ name: released.name, released }))
  ]
  if (entries.length < 2) {
    return []
  }
  const { pairs } = detectReadLayout(entries.map(({ name }) => ({ name })))
  return pairs.flatMap(([first, second]): ReverseJoin[] => {
    const target = entries[first].group
    const { group: source, released } = entries[second]
    if (!target) {
      return []
    }
    if (released) {
      return [{ kind: 'released', target: target.key, file: released }]
    }
    if (!source) {
      return []
    }
    if (hasNoItem(source)) {
      return [{ kind: 'take', target: target.key, source: source.key }]
    }
    return target.item.status === 'created' && source.item.status === 'created' && source.item.itemId
      ? [{ kind: 'absorb', target: target.key, source: source.key }]
      : []
  })
}

const releasedFileKey = (serverFileId: string) => `server:${serverFileId}`

/** The draft with the joins made: each target takes its R2, a target with an item through the attach op. */
export const withReverseJoins = (draft: DraftState, joins: readonly ReverseJoin[]): DraftState => {
  if (joins.length === 0) {
    return draft
  }
  const groups = { ...draft.groups }
  const files = { ...draft.files }
  const fileOrder = [...draft.fileOrder]
  let releasedFiles = [...(draft.releasedFiles ?? [])]
  const joinedReleased: string[] = []
  for (const join of joins) {
    const target = groups[join.target]
    if (!target || target.reverse) {
      continue
    }
    let reverse: string
    let absorbItemId: string | undefined
    if (join.kind === 'released') {
      const { serverFileId, name, size } = join.file
      if (!releasedFiles.some((entry) => entry.serverFileId === serverFileId)) {
        continue
      }
      reverse = releasedFileKey(serverFileId)
      const file: WizardFile = {
        name,
        size: size ?? 0,
        lastModified: 0,
        key: reverse,
        kind: 'fastq',
        fromServer: true,
        ...(files[target.forward]?.platform ? { platform: files[target.forward].platform } : {}),
        upload: { status: 'uploaded', progress: 1, serverFileId }
      }
      files[reverse] = file
      if (!fileOrder.includes(reverse)) {
        fileOrder.push(reverse)
      }
      releasedFiles = releasedFiles.filter((entry) => entry.serverFileId !== serverFileId)
      joinedReleased.push(serverFileId)
    } else {
      const source = groups[join.source]
      if (!source || source.reverse) {
        continue
      }
      reverse = source.forward
      absorbItemId = join.kind === 'absorb' ? source.item.itemId : undefined
      delete groups[join.source]
    }
    groups[join.target] = {
      ...target,
      reverse,
      ...(target.item.status === 'created' ? { reverseAttach: { status: 'pending', ...(absorbItemId ? { absorbItemId } : {}) } } : {})
    }
  }
  return {
    ...draft,
    groups,
    files,
    fileOrder,
    releasedFiles: releasedFiles.length > 0 ? releasedFiles : undefined,
    removals: withRemovalsRetried(draft.removals, joinedReleased)
  }
}

export type JoinPreview = {
  /** Uploads no sample uses that join a sample as its R2. */
  join: number
  /** Samples of their own that become another sample's R2. */
  absorb: number
  /** Samples and negative controls left waiting for their R2 file. */
  waiting: WizardSampleGroup[]
}

/** What making the joins would do, counted for the line shown before it happens. */
export const reverseJoinPreview = (draft: DraftState): JoinPreview => {
  const joins = planReverseJoins(draft)
  const sources = joins.flatMap((join) => (join.kind === 'released' ? [] : [join.source]))
  const joined = new Set([...sources, ...joins.map((join) => join.target)])
  const waiting = placedGroups(draft).filter((group) => !group.reverse && !singleEnded(draft, group) && !joined.has(group.key))
  return { join: joins.length - sources.length, absorb: sources.length, waiting }
}

/** "3 R2 files will join their samples; 2 separate samples become R2 files; 1 sample then waits for its R2 file". */
export const joinPreviewText = (preview: JoinPreview): string =>
  [
    preview.join > 0 && `${plural(preview.join, 'R2 file')} will join ${preview.join === 1 ? 'its sample' : 'their samples'}`,
    preview.absorb > 0 && `${plural(preview.absorb, 'separate sample')} ${preview.absorb === 1 ? 'becomes an R2 file' : 'become R2 files'}`,
    preview.waiting.length > 0 &&
      `${countItems(preview.waiting)} then ${preview.waiting.length === 1 ? 'waits for its R2 file' : 'wait for their R2 files'}`
  ]
    .filter((part): part is string => !!part)
    .join('; ')

/** The paired-end format a merged order switches to, when its platform takes one. */
export const pairedFormatFor = (draft: DraftState): SampleConfigurationTypeKey | undefined => {
  const value = draft.settings?.sampleConfiguration.value
  if (!value || !SampleConfigurationTypes[value].impliesMerged) {
    return undefined
  }
  const paired = layoutSwitchOf(value)
  const platform = draft.discovery?.platform
  return paired && isSampleConfigurationAllowedForPlatform(paired, platform && platform !== 'UNKNOWN' ? platform : null)
    ? paired
    : undefined
}

export type HalvesHint = { reason: string; preview: JoinPreview }

/**
 * Merged reads the files suggest are R1 halves whose R2 are missing: raw reads of a fixed instrument length
 * without mates, samples whose names pair up as R1 and R2, or (with no reads at hand) names marking every
 * file as R1. A recommendation only: the switch is the lab's.
 */
export const r1HalvesHint = (draft: DraftState): HalvesHint | undefined => {
  if (!draft.orderId || draft.submission || !pairedFormatFor(draft)) {
    return undefined
  }
  const groups = placedGroups(draft)
  if (groups.length === 0 || groups.some((group) => singleEnded(draft, group))) {
    return undefined
  }
  const preview = reverseJoinPreview(draft)
  const unmated = draft.discovery?.layout.unmated
  if (unmated?.mate === 1 && unmated.rawLength) {
    return { reason: `They look like raw R1 reads (all ${unmated.rawLength} bp), not merged ones.`, preview }
  }
  if (preview.absorb > 0) {
    return {
      reason: `By their names, ${plural(preview.absorb, 'sample')} ${preview.absorb === 1 ? 'is the R2 file of another sample' : 'are the R2 files of other samples'}.`,
      preview
    }
  }
  if (!draft.discovery && groups.every((group) => nameMate(draft.files[group.forward]?.name ?? '') === 1)) {
    return { reason: 'Their file names mark every file as an R1 file.', preview }
  }
  return undefined
}

export const R1_HALVES_ACK = 'r1-halves-hint'

/** The hint, unless the lab put it away. */
export const openHalvesHint = (draft: DraftState): HalvesHint | undefined =>
  (draft.acknowledged ?? []).includes(R1_HALVES_ACK) ? undefined : r1HalvesHint(draft)

/**
 * Dropped files that are the R2 of samples missing theirs, by the R1/R2 naming: they join those samples
 * rather than wait for a mate among the files dropped with them.
 */
export const pairDroppedWithWaiting = (
  draft: DraftState,
  dropped: readonly { key: string; name: string }[]
): { groupKey: string; reverse: string }[] => {
  const targets = groupsMissingReverse(draft).filter((group) => group.item.status !== 'creating' && !!draft.files[group.forward])
  if (targets.length === 0 || dropped.length === 0) {
    return []
  }
  const names = [...targets.map((group) => draft.files[group.forward].name), ...dropped.map((file) => file.name)]
  const { pairs } = detectReadLayout(names.map((name) => ({ name })))
  return pairs.flatMap(([first, second]) =>
    first < targets.length && second >= targets.length
      ? [{ groupKey: targets[first].key, reverse: dropped[second - targets.length].key }]
      : []
  )
}
